"use client"

import { useState } from "react"
import type { Hero, HeroSkill } from "@/types/hero.types"
import { getSkillLevelStats, canUpgradeSkill } from "@/systems/skill-system"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip"

interface SkillUpgradeButtonProps {
  hero: Hero | null
  onUpgrade: (skillId: string) => void
  isDesktop?: boolean
  pillarWidth?: number
  showTutorial?: boolean
}

const SkillTooltip = ({ skill, hero }: { skill: HeroSkill; hero: Hero }) => {
  // If skill is at level 0 (no points spent), show first level stats only
  const isUnleveled = skill.currentLevel === 0
  // For unleveled skills, show first level stats as the "current" display
  const stats = isUnleveled ? skill.levels[0] : getSkillLevelStats(skill)
  // Only show "next" stats if skill has been leveled and can still upgrade
  const nextStats = !isUnleveled && skill.currentLevel < skill.levels.length ? skill.levels[skill.currentLevel] : null
  const isMaxLevel = skill.currentLevel >= skill.maxLevel
  const hasCooldown = stats.cooldown > 0
  const canUpgrade = canUpgradeSkill(hero, skill.id)

  // Check if this is Dwarven Treasury (has interestBonus)
  const hasInterestBonus = stats.interestBonus !== undefined
  // Check if this is See the Wind (has critChance/critMultiplier)
  const hasCritStats = stats.critChance !== undefined
  // Check if this is Empower Armor (has healOnHitChance)
  const hasHealOnHit = stats.healOnHitChance !== undefined
  const isPassive = skill.type === "passive"

  // Check if this skill is blocked because it was the most recently upgraded
  const isBlockedByRecentUpgrade = () => {
    if (skill.lastUpgradedAtLevel === undefined) return false

    // Find the most recently upgraded skill
    let mostRecentUpgradeLevel = 0
    let mostRecentlyUpgradedSkillId: string | null = null

    for (const s of hero.skills) {
      if (s.lastUpgradedAtLevel !== undefined && s.lastUpgradedAtLevel > mostRecentUpgradeLevel) {
        mostRecentUpgradeLevel = s.lastUpgradedAtLevel
        mostRecentlyUpgradedSkillId = s.id
      }
    }

    return mostRecentlyUpgradedSkillId === skill.id
  }
  const blockedByRecentUpgrade = isBlockedByRecentUpgrade()

  return (
    <div className="w-[140px] p-1.5">
      {/* Header */}
      <div className="flex items-center justify-between mb-0.5">
        <span className="font-bold text-amber-400 text-[11px]">{skill.name}</span>
        <span className="text-[9px] text-amber-200/70">Lv{skill.currentLevel}</span>
      </div>

      {/* Passive indicator */}
      {isPassive && (
        <div className="mb-1">
          <span className="text-[9px] text-cyan-400 font-semibold">Passive</span>
        </div>
      )}

      {/* Description */}
      <p className="text-[9px] text-gray-300 leading-tight mb-1">{skill.description}</p>

      {/* Stats */}
      <div className="border-t border-amber-600/30 pt-1 space-y-0.5 text-[9px]">
        {hasCritStats ? (
          <>
            {/* Critical hit stats for See the Wind */}
            <div className="flex justify-between">
              <span className="text-gray-500">Crit Chance</span>
              <span className="text-cyan-400 font-semibold">
                {((stats.critChance || 0) * 100).toFixed(0)}%
                {nextStats?.critChance !== undefined && (
                  <span className="text-green-400"> → {(nextStats.critChance * 100).toFixed(0)}%</span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Crit DMG</span>
              <span className="text-red-400 font-semibold">
                {stats.critMultiplier || 0}x
                {nextStats?.critMultiplier !== undefined && (
                  <span className="text-green-400"> → {nextStats.critMultiplier}x</span>
                )}
              </span>
            </div>
          </>
        ) : hasInterestBonus ? (
          <>
            {/* Interest bonus stats for Dwarven Treasury - pure passive, no costs */}
            <div className="flex justify-between">
              <span className="text-gray-500">Interest Bonus</span>
              <span className="text-amber-400 font-semibold">
                +{((stats.interestBonus || 0) * 100).toFixed(1)}%
                {nextStats?.interestBonus !== undefined && (
                  <span className="text-green-400"> → +{(nextStats.interestBonus * 100).toFixed(1)}%</span>
                )}
              </span>
            </div>
            <div className="text-[9px] text-gray-400 mt-1 italic">
              Applied automatically between waves
            </div>
          </>
        ) : hasHealOnHit ? (
          <>
            {/* Empower Armor - heal on hit buff */}
            <div className="flex justify-between">
              <span className="text-gray-500">Heal Chance</span>
              <span className="text-green-400 font-semibold">
                {((stats.healOnHitChance || 0) * 100).toFixed(1)}%
                {nextStats?.healOnHitChance !== undefined && (
                  <span className="text-green-400"> → {(nextStats.healOnHitChance * 100).toFixed(1)}%</span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Heal Amount</span>
              <span className="text-green-400 font-semibold">
                {stats.healAmount || 0} HP
                {nextStats?.healAmount !== undefined && (
                  <span className="text-green-400"> → {nextStats.healAmount} HP</span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Damage Reduction</span>
              <span className="text-amber-300 font-semibold">
                {Math.round((stats.damageReduction || 0) * 100)}%
                {nextStats?.damageReduction !== undefined && (
                  <span className="text-green-400"> → {Math.round(nextStats.damageReduction * 100)}%</span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Duration</span>
              <span className="text-yellow-400">
                {((stats.buffDuration || 0) / 60).toFixed(0)}s
                {nextStats?.buffDuration !== undefined && (
                  <span className="text-green-400"> → {(nextStats.buffDuration / 60).toFixed(0)}s</span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Mana cost</span>
              <span className="text-blue-400">{stats.manaCost}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Cooldown</span>
              <span className="text-yellow-400">{(stats.cooldown / 60).toFixed(0)}s</span>
            </div>
          </>
        ) : (
          <>
            {/* Standard combat stats */}
            <div className="flex justify-between">
              <span className="text-gray-500">Damage</span>
              <span className="text-red-400 font-semibold">
                {stats.damage}
                {nextStats && <span className="text-green-400"> → {nextStats.damage}</span>}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Mana cost</span>
              <span className="text-blue-400">
                {stats.manaCost}
                {nextStats && nextStats.manaCost !== stats.manaCost && (
                  <span className="text-yellow-400"> → {nextStats.manaCost}</span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Cooldown</span>
              <span className={hasCooldown ? "text-yellow-400" : "text-green-400"}>
                {hasCooldown ? `${(stats.cooldown / 60).toFixed(1)}s` : "None"}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Hotkey - only for non-passive skills */}
      {!isPassive && (
        <div className="mt-1 pt-1 border-t border-amber-600/30 flex justify-between items-center">
          <span className="text-[8px] text-gray-500">Press</span>
          <kbd className="bg-gray-800 text-amber-300 px-1 py-0.5 rounded text-[9px] font-mono">{skill.hotkey}</kbd>
        </div>
      )}

      {/* Upgrade hint */}
      {!isMaxLevel && canUpgrade && (
        <div className="mt-1 text-[8px] text-green-400/80">Click to upgrade</div>
      )}
      {!isMaxLevel && !canUpgrade && blockedByRecentUpgrade && hero.skillPoints > 0 && (
        <div className="mt-1 text-[8px] text-amber-400/80">Upgrade another skill first</div>
      )}
      {isMaxLevel && (
        <div className="mt-1 text-[8px] text-amber-400/80">Max level reached</div>
      )}
    </div>
  )
}

const SkillUpgradeIcon = ({
  skill,
  hero,
  onUpgrade,
  scale = 1,
}: {
  skill: HeroSkill
  hero: Hero
  onUpgrade: (skillId: string) => void
  scale?: number
}) => {
  const canUpgrade = canUpgradeSkill(hero, skill.id)
  const isMaxLevel = skill.currentLevel >= skill.maxLevel
  const stats = getSkillLevelStats(skill)
  const nextStats = skill.currentLevel < skill.levels.length ? skill.levels[skill.currentLevel] : null

  return (
    <button
      onClick={() => canUpgrade && onUpgrade(skill.id)}
      disabled={!canUpgrade}
      className={`relative flex flex-col items-center p-1.5 rounded-lg transition-all ${
        canUpgrade ? "hover:scale-105 hover:bg-white/10 cursor-pointer" : "opacity-50 cursor-not-allowed"
      }`}
      style={{ minWidth: 56 * scale }}
    >
      {/* Skill icon */}
      <div
        className={`relative rounded-md flex items-center justify-center overflow-hidden ${canUpgrade ? "ring-1 ring-cyan-400" : ""}`}
        style={{
          width: 40 * scale,
          height: 40 * scale,
          backgroundColor: skill.iconColor,
          border: `1px solid ${canUpgrade ? "#22d3ee" : "#6b7280"}`,
        }}
      >
        {/* Skill icon image */}
        {skill.icon && (
          <img
            src={skill.icon}
            alt={skill.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {/* Level badge */}
        <span
          className="absolute -top-1 -left-1 bg-black/80 text-cyan-400 font-bold rounded-sm px-1"
          style={{ fontSize: 9 * scale }}
        >
          {skill.currentLevel}
        </span>

        {/* Plus icon for upgradable - blue rounded rectangle */}
        {canUpgrade && (
          <div className="absolute -bottom-1 -right-1 bg-cyan-500 rounded-md px-1 py-0.5">
            <svg
              width={10 * scale}
              height={10 * scale}
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M6 2.5V9.5M2.5 6H9.5" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
        )}

        {/* Max level indicator */}
        {isMaxLevel && (
          <span className="text-white font-bold" style={{ fontSize: 14 * scale }}>
            MAX
          </span>
        )}
      </div>

      {/* Skill name */}
      <span
        className="text-white/80 text-center mt-1 leading-tight"
        style={{ fontSize: 10 * scale, maxWidth: 60 * scale }}
      >
        {skill.name}
      </span>

      {/* Upgrade preview - for unlocked skills show current → next, for locked skills show only base value */}
      {canUpgrade && nextStats && (
        <span style={{ fontSize: 9 * scale }}>
          <span className="text-white">
            {stats.critChance !== undefined
              ? `${((stats.critChance || 0) * 100).toFixed(0)}%`
              : stats.interestBonus !== undefined
                ? `+${((stats.interestBonus || 0) * 100).toFixed(1)}%`
                : stats.healOnHitChance !== undefined
                  ? `${((stats.healOnHitChance || 0) * 100).toFixed(0)}%`
                  : `${stats.damage}`}
          </span>
          {/* Only show upgrade arrow for already unlocked skills (level > 0) */}
          {skill.currentLevel > 0 && (
            <span className="text-cyan-400">
              {stats.critChance !== undefined
                ? ` → ${((nextStats.critChance || 0) * 100).toFixed(0)}%`
                : stats.interestBonus !== undefined
                  ? ` → +${((nextStats.interestBonus || 0) * 100).toFixed(1)}%`
                  : stats.healOnHitChance !== undefined
                    ? ` → ${((nextStats.healOnHitChance || 0) * 100).toFixed(0)}%`
                    : ` → ${nextStats.damage}`}
            </span>
          )}
        </span>
      )}
    </button>
  )
}

export const SkillUpgradeButton = ({ hero, onUpgrade, isDesktop = false, pillarWidth = 0, showTutorial = false }: SkillUpgradeButtonProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  if (!hero) return null

  const hasSkillPoints = hero.skillPoints > 0
  const hasUpgradableSkills = hero.skills.some(
    (skill) => canUpgradeSkill(hero, skill.id)
  )

  // Don't show the collapsed button if no skill points or no upgradable skills
  // But keep the expanded menu open even after spending points (user closes manually)
  const showCollapsedButton = hasSkillPoints && hasUpgradableSkills && !isExpanded

  // Wrapper to close menu when last skill point is spent
  const handleUpgrade = (skillId: string) => {
    // If this is the last skill point, close the menu after upgrading
    if (hero.skillPoints === 1) {
      setIsExpanded(false)
    }
    onUpgrade(skillId)
  }

  return (
    <>
      {/* Expanded skill bar */}
      {isExpanded && (
        <div
          id="skill-upgrade-menu"
          className="fixed bottom-44 z-40 flex flex-col p-3 rounded-xl bg-black/90 border border-cyan-500/50 shadow-lg shadow-black/50 backdrop-blur-sm"
          style={{
            // Right-align to center column on desktop, to viewport edge on mobile
            right: isDesktop ? pillarWidth + 16 : 16,
            maxWidth: isDesktop ? "320px" : "calc(100vw - 32px)",
          }}
        >
          {/* Header with skill points and close button */}
          <div className="flex items-center justify-between mb-2 pb-2 border-b border-cyan-500/30">
            <div className="flex items-center gap-2">
              {/* Skill points badge in rounded square with blue pulse effect */}
              <span className="relative bg-white text-black font-bold text-base w-7 h-7 flex items-center justify-center rounded-md shadow-md">
                <span className="absolute inset-0 rounded-md bg-cyan-400 animate-ping opacity-40" />
                <span className="relative z-10">{hero.skillPoints}</span>
              </span>
              <span className="text-cyan-200/80 text-xs">
                unspent skill point{hero.skillPoints !== 1 ? "s" : ""}
              </span>
            </div>

            {/* Close button */}
            <button
              onClick={() => setIsExpanded(false)}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4L12 12M12 4L4 12" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Skill icons */}
          <TooltipProvider delayDuration={100}>
            <div className="flex items-center gap-1 flex-wrap justify-center">
              {hero.skills
                .filter((s) => s.type !== "ultimate" || s.isUnlocked)
                .map((skill) => (
                  <Tooltip key={skill.id}>
                    <TooltipTrigger asChild>
                      <div>
                        <SkillUpgradeIcon skill={skill} hero={hero} onUpgrade={handleUpgrade} />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-black/95 border border-amber-600/50 shadow-lg shadow-black/50 p-0">
                      <SkillTooltip skill={skill} hero={hero} />
                    </TooltipContent>
                  </Tooltip>
                ))}
            </div>
          </TooltipProvider>
        </div>
      )}

      {/* Indicator button using levelup-btn.png image */}
      {showCollapsedButton && (
        <button
          id="skill-upgrade-button"
          onClick={() => setIsExpanded(true)}
          className="fixed bottom-44 z-30 flex items-center justify-center hover:scale-105 active:scale-95 transition-all"
          style={{
            width: 56,
            height: 56,
            // On desktop: position at right edge of center column (pillarWidth + gameWidth - buttonWidth - margin)
            // On mobile: position at right edge with small margin
            right: isDesktop ? pillarWidth + 16 : 16,
          }}
        >
          {/* Blue ping effect */}
          <span className="absolute inset-0 rounded-lg bg-cyan-400 animate-ping opacity-40" />

          {/* Level up button image */}
          <img
            src="/images/levelup-btn.png"
            alt="Level Up"
            className="relative z-10 w-full h-full object-contain"
          />

          {/* Skill point count badge - white rounded rectangle with black text */}
          <span className="absolute -top-1 -right-1 bg-white text-black font-bold text-xs rounded-md px-1.5 py-0.5 min-w-[20px] flex items-center justify-center shadow-md z-20">
            {hero.skillPoints}
          </span>

          {/* Tutorial bubble - Level up skills */}
          {showTutorial && (
            <div
              className="absolute pointer-events-none z-40"
              style={{
                right: 64,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <div className="relative bg-black border border-white/50 rounded-lg px-3 py-2 text-white text-center whitespace-nowrap text-xs">
                <div>Level up skills</div>
                {/* Diamond pointer pointing RIGHT */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 -right-2 w-3 h-3 bg-black border-r border-b border-white/50 -rotate-45"
                />
              </div>
            </div>
          )}
        </button>
      )}
    </>
  )
}
